import { Controller } from "@hotwired/stimulus"

/**
 * Inline Edit Controller
 * 
 * Allows inline editing of text fields with click-to-edit functionality.
 * 
 * Usage:
 *   <div data-controller="inline-edit"
 *        data-inline-edit-url-value="/users/1"
 *        data-inline-edit-field-value="first_name">
 *     <span data-inline-edit-target="display">John</span>
 *     <input type="text" data-inline-edit-target="input" class="d-none" />
 *   </div>
 * 
 * Values:
 *   - url: The URL to send the update request to
 *   - field: The field name to update
 */
export default class extends Controller {
  static targets = ["display", "input"]
  static values = { 
    url: String,
    field: String
  }

  connect() {
    this.editing = false
  }

  edit() {
    if (this.editing) return
    
    this.editing = true
    this.displayTarget.classList.add("d-none")
    this.inputTarget.classList.remove("d-none")
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  cancel() {
    this.editing = false
    this.displayTarget.classList.remove("d-none")
    this.inputTarget.classList.add("d-none")
    // Reset input value to original
    this.inputTarget.value = this.displayTarget.textContent.trim()
  }

  async save() {
    if (!this.hasUrlValue || !this.hasFieldValue) return

    const value = this.inputTarget.value.trim()
    const originalValue = this.displayTarget.textContent.trim()

    // Don't save if value hasn't changed
    if (value === originalValue) {
      this.cancel()
      return
    }

    // Create form data
    const formData = new FormData()
    formData.append(`user[${this.fieldValue}]`, value)
    formData.append("_method", "PATCH")

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "text/vnd.turbo-stream.html"
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        // Update display value
        this.displayTarget.textContent = value || this.displayTarget.textContent
        this.editing = false
        this.displayTarget.classList.remove("d-none")
        this.inputTarget.classList.add("d-none")
      } else {
        // Handle error
        alert("Failed to update. Please try again.")
        this.cancel()
      }
    } catch (error) {
      console.error("Error updating:", error)
      alert("An error occurred. Please try again.")
      this.cancel()
    }
  }

  handleKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    }
  }
}

